/**
 * 报告保存为 API 两个独立字段：data（active_members、sections）、
 * static_data（气温、天气、总监）。本模块负责解析/归一与兼容旧格式。
 */

/** @return {object} */
function emptyStaticShape() {
  return {
    tmpl_l: '',
    tmpl_h: '',
    weather: '',
    charger: '',
  };
}

/** @return {object} */
export function emptyReportDataObject() {
  return {
    active_members: '',
    sections: [],
  };
}

/** @return {string} */
export function emptyReportDataString() {
  return JSON.stringify(emptyReportDataObject(), null, 2);
}

/** @return {string} */
export function emptyStaticDataString() {
  return JSON.stringify(emptyStaticShape(), null, 2);
}

/**
 * @param {object} o
 * @return {object}
 */
export function normalizeReportDataObject(o) {
  const src = o && typeof o === 'object' && !Array.isArray(o) ? o : {};
  let sections = [];
  if (Array.isArray(src.sections)) {
    sections = src.sections.slice();
  }
  let activeMembers = '';
  if (src.active_members !== undefined && src.active_members !== null) {
    activeMembers = String(src.active_members);
  }
  return {active_members: activeMembers, sections};
}

/**
 * @param {object} o
 * @return {object}
 */
export function normalizeStaticDataObject(o) {
  const src = o && typeof o === 'object' && !Array.isArray(o) ? o : {};
  const pick = (k) => {
    const v = src[k];
    return v != null && String(v) !== '' ? String(v) : '';
  };
  return {
    tmpl_l: pick('tmpl_l'),
    tmpl_h: pick('tmpl_h'),
    weather: pick('weather'),
    charger: pick('charger'),
  };
}

/**
 * @param {string} s
 * @return {string|null}
 */
export function normalizeReportDataString(s) {
  try {
    const o = s && String(s).trim() ? JSON.parse(s) : {};
    return JSON.stringify(normalizeReportDataObject(o), null, 2);
  } catch (e) {
    return null;
  }
}

/**
 * @param {string} s
 * @return {string|null}
 */
export function normalizeStaticDataString(s) {
  try {
    const o = s && String(s).trim() ? JSON.parse(s) : {};
    return JSON.stringify(normalizeStaticDataObject(o), null, 2);
  } catch (e) {
    return null;
  }
}

/**
 * 从接口 record 合并 data / static_data（兼容曾错误地把二者塞进同一 JSON）
 * @param {*} dataField 接口的 data
 * @param {*} staticField 接口的 static_data
 * @return {{ dataObj: object, staticObj: object }}
 */
export function coalesceReportFieldsFromApi(dataField, staticField) {
  let d = dataField;
  if (typeof d === 'string' && String(d).trim()) {
    try {
      d = JSON.parse(d);
    } catch (e) {
      d = {};
    }
  }
  if (d == null || typeof d !== 'object' || Array.isArray(d)) {
    d = {};
  }

  let s = staticField;
  if (typeof s === 'string' && String(s).trim()) {
    try {
      s = JSON.parse(s);
    } catch (e) {
      s = {};
    }
  }
  if (s == null || typeof s !== 'object' || Array.isArray(s)) {
    s = {};
  }

  // 曾错误：仅 data 里存了 { data, static_data }
  const nestedWrong =
    d.data !== undefined &&
    d.static_data !== undefined &&
    !Array.isArray(d.sections);
  if (nestedWrong) {
    s = {...normalizeStaticDataObject(d.static_data), ...s};
    d = d.data && typeof d.data === 'object' ? d.data : {};
  }

  // data 里误带了 static_data
  const sdNested =
    d.static_data && typeof d.static_data === 'object' &&
    !Array.isArray(d.static_data);
  if (sdNested) {
    s = {...normalizeStaticDataObject(d.static_data), ...s};
    const rest = {...d};
    delete rest.static_data;
    d = rest;
  }

  const STATIC_KEYS = ['tmpl_l', 'tmpl_h', 'weather', 'charger'];
  STATIC_KEYS.forEach((k) => {
    if (d[k] !== undefined) {
      s[k] = d[k];
      delete d[k];
    }
  });

  return {
    dataObj: normalizeReportDataObject(d),
    staticObj: normalizeStaticDataObject(s),
  };
}

/**
 * @param {string} dataStr JSON of data 部分
 * @return {boolean}
 */
export function reportDataHasSections(dataStr) {
  try {
    const o = dataStr && String(dataStr).trim() ? JSON.parse(dataStr) : {};
    const n = normalizeReportDataObject(o);
    return Array.isArray(n.sections) && n.sections.length > 0;
  } catch (e) {
    return false;
  }
}

/**
 * @param {object} obj
 * @return {string}
 */
export function stringifyReportData(obj) {
  return JSON.stringify(normalizeReportDataObject(obj), null, 2);
}

/**
 * @param {object} obj
 * @return {string}
 */
export function stringifyStaticData(obj) {
  return JSON.stringify(normalizeStaticDataObject(obj), null, 2);
}
